import { FC, useState, useEffect, useRef, useCallback } from 'react'
import { X, Copy, Check, Brain, Sparkles, GripVertical } from 'lucide-react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'

interface ThinkingSidePanelProps {
  content: string
  durationMs?: number
  isStreaming?: boolean
  onClose: () => void
}

// Default width reduced by 33% (from 500px to 335px)
const DEFAULT_WIDTH = 335
const MIN_WIDTH = 280
const MAX_WIDTH = 600

/**
 * Resizable side panel for expanded thinking/reasoning content
 * Clinical design with draggable resize handle
 */
export const ThinkingSidePanel: FC<ThinkingSidePanelProps> = ({
  content,
  durationMs,
  isStreaming = false,
  onClose
}) => {
  const [copied, setCopied] = useState(false)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [isResizing, setIsResizing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [content, isStreaming])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  // Keyboard shortcut: Esc to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Resize handlers
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    // Add class to body to prevent text selection during resize
    document.body.classList.add('resize-ew-active')

    const handleMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return

      // Calculate new width based on mouse position from right edge
      const rect = panelRef.current.getBoundingClientRect()
      const newWidth = rect.right - e.clientX

      // Clamp to min/max
      setWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.classList.remove('resize-ew-active')
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.classList.remove('resize-ew-active')
    }
  }, [isResizing])

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  return (
    <div
      ref={panelRef}
      className="relative flex h-full flex-col border-l bg-background animate-fade-in"
      style={{ width: `${width}px` }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={startResize}
        className={cn(
          "absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10",
          "group flex items-center justify-center",
          "hover:bg-clinical-teal/20 transition-colors",
          isResizing && "bg-clinical-teal/30"
        )}
      >
        {/* Visible grip indicator */}
        <div className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2",
          "w-4 h-12 rounded-full",
          "bg-surface-elevated border border-border/50",
          "flex items-center justify-center",
          "opacity-0 group-hover:opacity-100 transition-opacity",
          "shadow-sm",
          isResizing && "opacity-100 border-clinical-teal/50"
        )}>
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Brain className={cn(
              "h-4 w-4 text-clinical-teal",
              isStreaming && "ekg-brain"
            )} />
            {isStreaming && (
              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-clinical-cyan opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-clinical-cyan" />
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold">
            {isStreaming ? 'Reasoning...' : 'Reasoning'}
          </h3>
          <span className={cn(
            "rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
            "bg-clinical-teal/15 text-clinical-teal"
          )}>
            {isStreaming ? 'Live' : 'Done'}
          </span>
        </div>

        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 w-7 p-0"
            title="Copy to clipboard"
            aria-label="Copy reasoning to clipboard"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-clinical-sage" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-7 w-7 p-0"
            title="Close panel (Esc)"
            aria-label="Close reasoning panel"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-clinical-teal/20 scrollbar-track-transparent"
      >
        {/* Subtle decorative element */}
        <div className="absolute top-16 right-4 opacity-[0.03] pointer-events-none">
          <Sparkles className="h-24 w-24 text-clinical-teal" />
        </div>

        <div className={cn(
          "relative text-[13px] text-muted-foreground leading-relaxed",
          "whitespace-pre-wrap",
          isStreaming && "thinking-cursor"
        )}>
          {content || 'Processing...'}
        </div>

        {/* Streaming indicator */}
        {isStreaming && (
          <div className="mt-3 flex items-center gap-2 text-clinical-teal">
            <div className="typing-indicator">
              <span />
              <span />
              <span />
            </div>
            <span className="text-[11px]">Reasoning in progress...</span>
          </div>
        )}
      </div>

      {/* Footer with metadata - compact */}
      <div className="border-t bg-gradient-to-r from-clinical-teal/5 to-clinical-cyan/5 px-4 py-2 text-[11px] text-muted-foreground">
        <div className="flex flex-wrap gap-3">
          {durationMs && !isStreaming && (
            <span>{formatDuration(durationMs)}</span>
          )}
          <span>{content.length} chars</span>
          <span>{(content.match(/\n/g) || []).length + 1} lines</span>
        </div>
      </div>
    </div>
  )
}
